import { config } from '../../../config';
import { useCityBoundary } from '../../features/queries/getCityBoundary';
import {
  useGeoLocation,
  type GeoLocation,
} from '../../providers/LocationProvider';
import { __ } from '../../utils';
import GeoMap from '../GeoMap';
import {
  Alert,
  Box,
  Button,
  FormLabel,
  Stack,
  useTheme,
} from '@presov.sk/presov-ui-react';
import React from 'react';
import { Field, useForm, useFormState } from 'react-final-form';

const defaultPoint: GeoLocation = [
  config.DEFAULT_POSITION[0],
  config.DEFAULT_POSITION[1],
];

type Props = {
  name: string;
  label?: string;
  defaultValue?: () => Promise<GeoLocation | undefined>;
  initialPosition?: GeoLocation;
  searchInitialPosition?: boolean;
  deleteLocationLabel?: string;
  deleteWithoutWarningLabel?: boolean;
  /**
   * Pole, ktoré sa **polohu zariadenia nepýta vôbec** - ani pri načítaní, ani
   * pri „Vybrať polohu na mape" (tam ide rovno na ďalší zdroj). Viď
   * `withoutDeviceLocation` v `ArcgisMap`; má ho filter lokality
   * v administrácii.
   */
  withoutDeviceLocation?: boolean;
};

export type FormMapFieldRef = {
  changeMapCenter: (position: GeoLocation) => void;
  navigateTo: (position: GeoLocation, askForPermission: boolean) => void;
  importMetadata: (position: GeoLocation) => void;
};

const FormMapField = React.forwardRef<FormMapFieldRef, Props>(
  (
    {
      name,
      label,
      defaultValue,
      initialPosition,
      searchInitialPosition = true,
      deleteLocationLabel = __('Odstraniť polohu?'),
      deleteWithoutWarningLabel,
      withoutDeviceLocation = false,
    },
    ref,
  ) => {
    const theme = useTheme();
    const { locationStatus, getCurrentPosition } = useGeoLocation();
    const { isInside } = useCityBoundary();
    const { change } = useForm();
    const { values } = useFormState();

    React.useImperativeHandle(ref, () => ({
      changeMapCenter,
      navigateTo,
      importMetadata: importLocationFromMetadata,
    }));

    const mapRef = React.useRef<HTMLArcgisMapElement>(null);
    const layerRef = React.useRef<unknown>(null);
    const onChangeRef = React.useRef<((value: GeoLocation) => void) | null>(
      null,
    );
    const currentValueRef = React.useRef<GeoLocation | null>(null);

    const [locationPickEnabled, setLocationPickEnabled] =
      React.useState<boolean>(searchInitialPosition);
    const [redirectOnMetadata, setRedirectOnMetadata] =
      React.useState<boolean>(true);
    const [outsideCity, setOutsideCity] = React.useState<boolean>(false);
    const [deviceOutsideCity, setDeviceOutsideCity] =
      React.useState<boolean>(false);

    /**
     * Poloha **zariadenia** mimo mesta nie je chyba používateľa - len sa
     * nachádza inde. Preto sa nezobrazí chyba, ale upozornenie, a mapa zostane
     * na Prešove, akoby povolená poloha nebola. Bez toho upozornenia to
     * vyzeralo ako pokazená lokalizácia: mapa „ignorovala" polohu a nepovedala
     * prečo.
     */
    const isDeviceInCity = React.useCallback(
      (position: GeoLocation) => {
        const ok = isInside(position[0], position[1]);
        setDeviceOutsideCity(!ok);
        return ok;
      },
      [isInside],
    );

    /**
     * Poloha musí ležať na území mesta - a musí sa to overiť **na každej**
     * ceste, ktorou sa do formulára dostane, nie len pri kliknutí do mapy.
     * Cesty sú štyri: klik, poloha z prehliadača, výsledok hľadania a GPS
     * z fotky. Práve tá posledná je zákerná - fotka odfotená na dovolenke
     * dovtedy umiestnila podnet tam, kde vznikla.
     *
     * Bez načítanej hranice pustí všetko: tvrdú poistku drží API, toto je len
     * to, aby používateľ nedostal 400 až po odoslaní formulára.
     */
    const acceptLocation = React.useCallback(
      (position: GeoLocation) => {
        const ok = isInside(position[0], position[1]);
        setOutsideCity(!ok);
        return ok;
      },
      [isInside],
    );

    React.useEffect(() => {
      if (locationStatus === 'DENY' || locationStatus === 'UNAVAILABLE') {
        setLocationPickEnabled(false);
      }
    }, [locationStatus]);

    const changeMapCenter = (position: GeoLocation) => {
      if (mapRef.current) {
        mapRef.current.center = [position[0], position[1]];
      }

      renderLocationOverlay(position);
      setLocationPickEnabled(true);

      if (onChangeRef.current) {
        onChangeRef.current(position);
      }
    };

    const navigateTo = (
      position: GeoLocation,
      askForPermission: boolean = true,
    ) => {
      if (position.length < 2) {
        return;
      }
      setLocationPickEnabled(true);
      renderLocationOverlay(position);

      if (mapRef.current) {
        mapRef.current.view
          .goTo({
            center: position,
          })
          .catch((error) => {
            // Handle interrupted goTo calls silently as they are expected behavior
            if (error.name === 'view:goto-interrupted') {
              console.debug(
                'Map navigation was interrupted, this is normal behavior',
              );
            } else {
              console.error('Error during map navigation:', error);
            }
          });

        const sameLocation =
          currentValueRef.current &&
          position[0] === currentValueRef.current[0] &&
          position[1] === currentValueRef.current[1];

        if (askForPermission) {
          if (sameLocation) {
            change('draftLocation', undefined);
          } else {
            change('draftLocation', position);
          }
        }
      }
    };

    const importLocationFromMetadata = (position: GeoLocation) => {
      if (!redirectOnMetadata || locationPickEnabled) {
        return;
      }

      // GPS z fotky mimo mesta sa ticho ignoruje - je to len návrh polohy,
      // nie voľba používateľa, takže naň netreba ani hlásiť chybu.
      if (!isInside(position[0], position[1])) {
        return;
      }

      setRedirectOnMetadata(false);
      navigateTo(position, false);
      onChangeRef.current?.(position);
    };

    const renderLocationOverlay = React.useCallback(
      async (position: GeoLocation) => {
        // Add a point graphic at the position
        if (layerRef.current) {
          const [longitude, latitude] = position;

          if (!longitude || !latitude) return;

          try {
            // Dynamically import ArcGIS classes
            const [{ default: Graphic }, { default: Point }] =
              await Promise.all([
                import('@arcgis/core/Graphic'),
                import('@arcgis/core/geometry/Point'),
              ]);

            const graphic = new Graphic({
              geometry: new Point({ longitude, latitude }),
              symbol: {
                type: 'picture-marker',
                url: '/locationPin.svg',
                width: '32px',
                height: '32px',
                yoffset: '14px',
              },
            });

            const layer = layerRef.current as {
              graphics: {
                length: number;
                at: (index: number) => { geometry: unknown };
              };
              add: (graphic: unknown) => void;
            };
            if (!layer.graphics.length) {
              layer.add(graphic);
            } else {
              layer.graphics.at(0).geometry = graphic.geometry;
            }
          } catch (error) {
            console.error('Chyba pri načítaní ArcGIS modulov:', error);
          }
        }
      },
      [],
    );

    // Bez polohy zariadenia sa na ňu nečaká: stav by v `CHECK` zostal navždy
    // (nikto sa nepýta) a hlášky aj „Odstrániť polohu" pod mapou by sa
    // nevykreslili nikdy.
    const isLoading = !withoutDeviceLocation && locationStatus === 'CHECK';
    const showDeleteButton = isLoading !== locationPickEnabled;

    return (
      <Field name={name}>
        {({ input: { value, onChange } }) => {
          onChangeRef.current = onChange;
          currentValueRef.current = value;

          const DeleteLocationButton = (
            <Button
              fullWidth
              variant={deleteWithoutWarningLabel ? 'outlined' : 'contained'}
              onClick={() => {
                setLocationPickEnabled(false);
                change('draftLocation', null);
                onChange(null);
                renderLocationOverlay([0, 0]);

                setRedirectOnMetadata(false);
                if (mapRef.current) {
                  mapRef.current.center = defaultPoint;
                }
              }}
            >
              {__('Odstrániť polohu')}
            </Button>
          );

          return (
            <Stack spacing={1}>
              <Stack>
                {label && <FormLabel htmlFor={name}>{label}</FormLabel>}
                {deviceOutsideCity && (
                  // Nepatrí do spodného pásu nad mapou: ten drží prechodné
                  // výzvy k práve vybranému bodu („nastaviť polohu?"), kým toto
                  // je stály fakt o používateľovi. Nad mapou tak nič neprekrýva
                  // a nesúperí s nimi o to isté miesto.
                  <Alert
                    severity='warning'
                    icon={false}
                    sx={{ mb: 1 }}
                    onClose={() => setDeviceOutsideCity(false)}
                  >
                    {__(
                      'Pozor, nenachádzate sa na území mesta Prešov. Mapa zostala na Prešove - miesto podnetu vyberte kliknutím do mapy.',
                    )}
                  </Alert>
                )}
                <Box
                  sx={{
                    position: 'relative',
                  }}
                >
                  {!locationPickEnabled && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        height: '100%',
                        width: '100%',
                        backgroundColor: 'rgba(255, 255, 255, 0.74)',
                        zIndex: 1000,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                      }}
                    >
                      <Button
                        onClick={async () => {
                          // Ponuka je zoradená od najpresnejšieho zdroja, ale
                          // každý z nich môže ukazovať mimo Prešova - poloha
                          // z prehliadača, keď je používateľ na cestách, a GPS
                          // z fotky, keď ju odfotil na dovolenke. Zdroj mimo
                          // mesta sa preto **preskočí** a ide sa na ďalší;
                          // nastaviť ho a hneď na to vypísať chybu by bolo
                          // horšie než ho nepoužiť.

                          // 1. Ak je povolená poloha, tak aktuálna poloha
                          const currentPosition = withoutDeviceLocation
                            ? undefined
                            : await getCurrentPosition();
                          if (
                            currentPosition &&
                            isDeviceInCity(currentPosition)
                          ) {
                            changeMapCenter(currentPosition);
                            return;
                          }

                          // 2. Ak je v podnete nahratý súbor s lokáciou, tak nájdi lokáciu v prvom takom súbore
                          if (defaultValue) {
                            const locationFromFile = await defaultValue();
                            if (
                              locationFromFile &&
                              isInside(locationFromFile[0], locationFromFile[1])
                            ) {
                              changeMapCenter(locationFromFile);
                              return;
                            }
                          }

                          // 3. Default position
                          changeMapCenter(defaultPoint);
                        }}
                      >
                        {__('Vybrať polohu na mape')}
                      </Button>
                    </Box>
                  )}
                  <GeoMap
                    sx={{
                      height: 400,
                      border: `1px solid ${theme.palette.grey[400]}`,
                      overflow: 'hidden',
                      borderRadius: theme.typography.pxToRem(
                        Number(theme.shape.borderRadius),
                      ),
                    }}
                    ref={mapRef}
                    withoutDeviceLocation={withoutDeviceLocation}
                    onReady={async ({ map }) => {
                      // Dynamically import GraphicsLayer
                      const { default: GraphicsLayer } =
                        await import('@arcgis/core/layers/GraphicsLayer');
                      layerRef.current = new GraphicsLayer({
                        title: __('Vybraný bod'),
                      });
                      map.add(
                        layerRef.current as import('@arcgis/core/layers/GraphicsLayer').default,
                      );

                      if (initialPosition) {
                        if (mapRef.current && initialPosition.length >= 2) {
                          mapRef.current.center = [
                            initialPosition[0],
                            initialPosition[1],
                          ];
                          renderLocationOverlay(initialPosition);
                          onChange([initialPosition[0], initialPosition[1]]);
                          setLocationPickEnabled(true);
                          navigateTo(initialPosition, false);
                        }
                      } else if (!withoutDeviceLocation) {
                        getCurrentPosition({ silent: true }).then((pos) => {
                          if (!pos || !locationPickEnabled) return;

                          // Táto cesta nastavuje polohu podnetu priamo, bez
                          // toho, aby na ňu používateľ klikol - mimo mesta by
                          // teda podnet dostal polohu, ktorú nikto nevybral.
                          if (!isDeviceInCity(pos)) return;

                          if (mapRef.current && pos.length >= 2) {
                            mapRef.current.center = [pos[0], pos[1]];
                            renderLocationOverlay(pos);
                            onChange([pos[0], pos[1]]);
                            setLocationPickEnabled(true);
                          }
                        });
                      }
                    }}
                    onClick={async ({ mapPoint }) => {
                      if (!layerRef.current) return;

                      const { longitude, latitude } = mapPoint;

                      // Mimo mesta sa značka ani neprekreslí - kliknutie do
                      // zašrafovanej plochy nemá čo nastaviť.
                      if (!acceptLocation([longitude, latitude])) return;

                      renderLocationOverlay([longitude, latitude]);
                      change('draftLocation', [longitude, latitude]);
                    }}
                    onAction={async () => {
                      getCurrentPosition().then((location) => {
                        // Tu je zdrojom poloha zariadenia, nie voľba
                        // používateľa, tak sa hlási upozornenie, nie chyba.
                        if (location && isDeviceInCity(location)) {
                          change('draftLocation', location);
                          navigateTo(location);
                        }
                      });
                    }}
                    disableAction={!!values.draftLocation}
                    onSearchResult={(detail) => {
                      const point = detail.result.feature.geometry;

                      if (point?.type === 'point') {
                        const long = point.longitude;
                        const lat = point.latitude;

                        if (!acceptLocation([long, lat])) return;

                        renderLocationOverlay([long, lat]);
                        change('draftLocation', [long, lat]);
                      }
                    }}
                    initialZoom={15}
                    search
                    hideUserLocation
                    disablePopup
                    locationIndicator={false}
                  />
                  {!isLoading && (
                    <>
                      {outsideCity && (
                        // Leží nad ostatnými hláškami toho istého pásu:
                        // „kliknite na mapu" a „nastaviť polohu?" v tej chvíli
                        // hovoria o niečom, čo sa nestalo.
                        <Box
                          width={'100%'}
                          position={'absolute'}
                          bottom={0}
                          left={0}
                          zIndex={1002}
                        >
                          <Alert
                            severity='error'
                            icon={false}
                            onClose={() => setOutsideCity(false)}
                          >
                            {__(
                              'Podnet sa dá nahlásiť len na území mesta Prešov. Vyberte miesto vo vyznačenej časti mapy.',
                            )}
                          </Alert>
                        </Box>
                      )}

                      <Box
                        width={'100%'}
                        position={'absolute'}
                        bottom={0}
                        left={0}
                        zIndex={1001}
                        visibility={
                          locationPickEnabled && !values.draftLocation
                            ? 'visible'
                            : 'hidden'
                        }
                      >
                        <Alert severity='info' icon={false}>
                          {__(
                            'Ak nesedí poloha, kliknite na mapu alebo ju vyhľadajte.',
                          )}
                        </Alert>
                      </Box>

                      <Box
                        width={'100%'}
                        position={'absolute'}
                        bottom={0}
                        left={0}
                        zIndex={1001}
                        visibility={
                          locationPickEnabled && !!values.draftLocation
                            ? 'visible'
                            : 'hidden'
                        }
                      >
                        <Alert
                          severity='warning'
                          icon={false}
                          action={
                            <Stack direction='row' spacing={1}>
                              <Button
                                sx={{ width: 140 }}
                                color='error'
                                onClick={() => {
                                  if (
                                    value &&
                                    Array.isArray(value) &&
                                    value.length >= 2
                                  ) {
                                    navigateTo([value[0], value[1]]);
                                  }
                                  change('draftLocation', undefined);
                                }}
                              >
                                {__('Obnoviť polohu')}
                              </Button>
                              <Button
                                color='warning'
                                onClick={() => {
                                  if (
                                    values.draftLocation &&
                                    Array.isArray(values.draftLocation) &&
                                    values.draftLocation.length >= 2
                                  ) {
                                    navigateTo([
                                      values.draftLocation[0],
                                      values.draftLocation[1],
                                    ]);
                                  }

                                  onChange(values.draftLocation);
                                  change('draftLocation', undefined);
                                }}
                              >
                                {__('Potvrdiť')}
                              </Button>
                            </Stack>
                          }
                        >
                          {__('Nastaviť polohu podnetu?')}
                        </Alert>
                      </Box>
                    </>
                  )}
                </Box>
              </Stack>
              {showDeleteButton && (
                <Box>
                  {deleteWithoutWarningLabel ? (
                    DeleteLocationButton
                  ) : (
                    <Alert
                      severity='error'
                      icon={false}
                      action={DeleteLocationButton}
                    >
                      {deleteLocationLabel}
                    </Alert>
                  )}
                </Box>
              )}
            </Stack>
          );
        }}
      </Field>
    );
  },
);

FormMapField.displayName = 'FormMapField';

export default FormMapField;
